/**
 * POS reference numbers, in the format OHC agreed on 18 September 2026.
 *
 *   S 26 9 18 A 07
 *   | |  | |  | |
 *   | |  | |  | +-- sequence for that device on that day, 2 digits, 3 once it passes 99
 *   | |  | |  +---- device code, one letter, A = iPad 1, B = iPad 2, C = shop computer
 *   | |  | +------- day of month
 *   | |  +--------- month, 1 to 9 for January to September, A, B, C for October to December
 *   | +------------ last two digits of the year
 *   +-------------- what it is, S for a sale, B for a buyback
 *
 * The device code is what makes offline work safe. Two iPads with no internet cannot
 * produce the same reference, because each one only ever writes its own letter. The
 * server never hands out these numbers, so a sale at a tradeshow with no signal still
 * gets its final reference immediately.
 *
 * A cancelled or reversed sale keeps its reference. Numbers are never reused, so gaps
 * in the sequence are normal and are not a sign that something is missing.
 */
export const ReferenceKind = {
    Sale: "S",
    Buyback: "B",
};
export class ReferenceError extends Error {
}
const SINGAPORE = "Asia/Singapore";
/**
 * The Singapore calendar date of an instant.
 *
 * A sale keyed at 00:30 on an iPad whose clock is on another timezone still belongs to
 * the Singapore day, which is the day the shop counts its money for.
 */
export function singaporeDate(at = new Date()) {
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: SINGAPORE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(at);
    const get = (type) => {
        const found = parts.find((part) => part.type === type);
        if (!found)
            throw new ReferenceError(`cannot read ${type} from the date`);
        return Number(found.value);
    };
    return { year: get("year"), month: get("month"), day: get("day") };
}
const MONTH_CODES = "123456789ABC";
export function monthCode(month) {
    const code = MONTH_CODES[month - 1];
    if (code === undefined)
        throw new ReferenceError(`month out of range: ${month}`);
    return code;
}
export function monthFromCode(code) {
    const index = MONTH_CODES.indexOf(code.toUpperCase());
    if (index < 0)
        throw new ReferenceError(`not a month code: ${JSON.stringify(code)}`);
    return index + 1;
}
/** A device code is a single letter. It is fixed for the life of the device and never reused. */
export function assertDeviceCode(code) {
    if (!/^[A-Z]$/.test(code)) {
        throw new ReferenceError(`device code must be one letter A to Z, got ${JSON.stringify(code)}`);
    }
    return code;
}
export function buildReference(parts) {
    const { kind, date, device, sequence } = parts;
    if (kind !== ReferenceKind.Sale && kind !== ReferenceKind.Buyback) {
        throw new ReferenceError(`unknown reference kind: ${JSON.stringify(kind)}`);
    }
    if (date.day < 1 || date.day > 31)
        throw new ReferenceError(`day out of range: ${date.day}`);
    if (date.year < 2000 || date.year > 2099) {
        throw new ReferenceError(`year out of range for a 2 digit year: ${date.year}`);
    }
    if (!Number.isInteger(sequence) || sequence < 1) {
        throw new ReferenceError(`sequence must be a whole number from 1: ${sequence}`);
    }
    assertDeviceCode(device);
    const year = String(date.year % 100).padStart(2, "0");
    const day = String(date.day).padStart(2, "0");
    const seq = String(sequence).padStart(2, "0");
    return `${kind}${year}${monthCode(date.month)}${day}${device}${seq}`;
}
const REFERENCE_PATTERN = /^([SB])(\d{2})([1-9ABC])(\d{2})([A-Z])(\d{2,})$/;
export function parseReference(reference) {
    const match = REFERENCE_PATTERN.exec(reference.trim().toUpperCase());
    if (!match)
        throw new ReferenceError(`not a reference: ${JSON.stringify(reference)}`);
    const [, kind, year, month, day, device, sequence] = match;
    const dayNumber = Number(day);
    if (dayNumber < 1 || dayNumber > 31)
        throw new ReferenceError(`day out of range: ${day}`);
    const sequenceNumber = Number(sequence);
    if (sequenceNumber < 1)
        throw new ReferenceError(`sequence out of range: ${sequence}`);
    return {
        kind,
        date: { year: 2000 + Number(year), month: monthFromCode(month), day: dayNumber },
        device,
        sequence: sequenceNumber,
    };
}
export function isReference(value) {
    try {
        parseReference(value);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Pull references out of a bank statement line.
 *
 * A PayNow credit arrives with the customer's own text, and the reference we asked the
 * staff to type sits somewhere inside it. Finding it is what lets a bank line attach
 * itself to the right sale without anyone hunting for it.
 */
export function findReferences(text) {
    const found = text.toUpperCase().match(/[SB]\d{2}[1-9ABC]\d{2}[A-Z]\d{2,}/g) ?? [];
    return [...new Set(found.filter(isReference))];
}
/**
 * The short code a PayNow sale is collected under: the device letter and that day's
 * number, A7 for the seventh sale on iPad A.
 *
 * Daren asked for it (29 September 2026): at a busy table, typing S26928A07 into OneCollect
 * for every customer is nine characters each time. Within one day the letter and the
 * number are already what tells two sales apart, and the bank line carries the day and the
 * amount anyway, so the three together still find the one sale. The full reference stays
 * on the sale and the invoice.
 */
export function paynowCode(reference) {
    const { device, sequence } = parseReference(reference);
    return `${device}${sequence}`;
}
/**
 * Short PayNow codes in a bank statement line: A7, or a07 as somebody might type it.
 *
 * Only a letter and one to three digits standing on their own count, so the insides of a
 * full reference, a date or an account number are never read as one. Written without a
 * lookbehind, because this file also runs on the iPad and older Safari cannot parse one.
 */
export function findPaynowCodes(text) {
    const found = new Map();
    for (const match of text.toUpperCase().matchAll(/(?:^|[^A-Z0-9])([A-Z])(\d{1,3})(?=[^A-Z0-9]|$)/g)) {
        const device = match[1];
        const sequence = Number(match[2]);
        if (!device || !(sequence >= 1))
            continue;
        found.set(`${device}${sequence}`, { device, sequence });
    }
    return [...found.values()];
}
//# sourceMappingURL=reference.js.map